'use client';

import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
  type ChangeEvent,
  type InputHTMLAttributes,
  type ReactNode,
  type Ref,
} from 'react';
import { IoClose } from 'react-icons/io5';
import { Button } from '../button/Button';
import { ButtonType, ButtonVariant } from '../button/Button.types';
import { cn } from '@/lib/styleUtils';
import {
  baseInputContainerVariants,
  inputBaseClasses,
  leftIconWrapperClasses,
  rightActionsWrapperClasses,
  clearButtonClasses,
  rightActionButtonClasses,
  rightIconWrapperClasses,
} from './BaseInput.style';
import type { InputValidator } from './validators';

/**
 * Transforms raw input value before it is stored / propagated.
 * Receives the current raw value from the browser and the previous formatted value.
 * Return the value that should be stored and passed to `onChange`.
 */
export type InputTypeFormatter = (value: string, previousValue: string) => string;

const DEFAULT_REQUIRED_MESSAGE = 'This field is required';
const DEFAULT_INVALID_MESSAGE = 'Invalid';

/**
 * Run validators in order and return the first error message or `null`.
 * Empty values are treated as valid when the field is not required.
 */
function runValidators(
  value: string,
  validators: InputValidator[],
  required?: boolean
): string | null {
  if (value === '' && !required) return null;
  for (const v of validators) {
    if (!v.validate(value)) return v.errorText;
  }
  return null;
}

/**
 * Low-level input component that focuses on behavior and value/validation logic.
 *
 * Responsibilities:
 * - Manage controlled vs uncontrolled value.
 * - Apply `formatter` to raw user input before updating state / firing `onChange`.
 * - Run `validators` + `required` rules and expose the combined error.
 * - Render icons / clear button / right action area, but leave label + helper text to `FieldWrapper`.
 */
export interface BaseInputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'className' | 'value' | 'onChange'
> {
  /** Optional visual label; primarily used by the higher-level `Input` wrapper. */
  label?: string;

  /** When true, empty values are considered invalid and show `requiredMessage`. */
  required?: boolean;

  /**
   * Visual-only required `*` marker (handled by `FieldWrapper`). Does NOT set
   * the native `required` attribute or run required validation — use for
   * "at least one of these fields" groups. Ignored by `BaseInput` itself.
   */
  requiredMarker?: boolean;

  /** Message shown when a required field is left empty. */
  requiredMessage?: string;

  /** Extra information rendered next to the label (string tooltip or custom node). */
  infoTooltip?: string | ReactNode;

  /** Icon rendered on the left inside the input container. */
  leftIcon?: ReactNode;

  /** Icon rendered on the right inside the input container (next to clear / buttons). */
  rightIcon?: ReactNode;

  /** Called when the optional clear button is clicked. */
  onClear?: () => void;

  /** Accessible label for the clear button. */
  clearAriaLabel?: string;

  /** Optional small action button rendered on the right side (e.g. “Apply”). */
  rightButton?: { label: string; onClick: () => void; 'data-cy'?: string };

  /** Helper text shown below the field via `FieldWrapper`. */
  helperText?: string;

  /**
   * Error coming from the parent:
   * - string → show this error message and mark field invalid.
   * - true   → mark field invalid but do not show a specific message.
   * - false/undefined → rely on required + validators.
   */
  error?: string | boolean;

  /** Custom class for the outer container (includes border, background, icons). */
  className?: string;

  /** Custom class applied to the actual `<input>` element. */
  inputClassName?: string;

  /** Additional container className, typically passed from the parent. */
  containerClassName?: string;

  /** Optional ref to the outer container div (useful for positioning or scrolling). */
  containerRef?: Ref<HTMLDivElement>;

  /**
   * Optional formatter that receives raw and previous values and returns
   * the value to store / emit. Useful for masks such as currency or phone numbers.
   * Prefer a stable reference (e.g. `useCallback`) to avoid re-creating handlers.
   */
  formatter?: InputTypeFormatter;

  /**
   * Validators that run on blur and, after first blur, on every change.
   * Validation is skipped when the field is empty and not required.
   */
  validators?: InputValidator[];

  /** Skip touched-on-blur. For trigger-style inputs (e.g. Select) whose blur is part of opening a menu, not "completed entry". */
  suppressBlurValidation?: boolean;

  /**
   * When provided together with `onChange`, the component behaves as a controlled input.
   * When omitted, the value is managed internally and emitted via `onChange`.
   */
  value?: string;

  /**
   * Change handler that always receives the formatted value.
   * The first argument is a synthetic event whose `target.value` is already formatted.
   */
  onChange?: (value: string, inputKey?: string, e?: ChangeEvent<HTMLInputElement>) => void;

  /**
   * Called whenever the effective validation error changes
   * (either from required rule or custom validators).
   * Used by the higher-level `Input` component to feed `FieldWrapper`.
   */
  onValidationChange?: (error: string | null) => void;

  /**
   * The inputkey for the input element.
   */
  inputKey?: string;

  /**
   * Optional ReactNode rendered on top of the input text.
   * When present, the input text is made transparent so the overlay is visible,
   * but the input still receives clicks, focus, and keyboard events.
   */
  overlay?: ReactNode;

  /** When true, removes the bottom padding reserved for helper/error text. */
  hideBottomPadding?: boolean;
}

export const BaseInput = forwardRef<HTMLInputElement, BaseInputProps>(function BaseInput(
  {
    label,
    required,
    requiredMessage = DEFAULT_REQUIRED_MESSAGE,
    infoTooltip,
    leftIcon,
    rightIcon,
    onClear,
    clearAriaLabel = 'Clear',
    rightButton,
    helperText,
    error: controlledError,
    className = '',
    inputClassName = '',
    containerClassName = '',
    containerRef,
    formatter,
    validators = [],
    suppressBlurValidation = false,
    onValidationChange,
    id: idProp,
    value: controlledValue,
    onChange: controlledOnChange,
    onBlur,
    onInvalid: onInvalidProp,
    pattern: restPattern,
    inputKey,
    overlay,
    hideBottomPadding: _hideBottomPadding,
    requiredMarker: _requiredMarker,
    ...rest
  },
  ref
) {
  const generatedId = useId();
  const id = idProp ?? generatedId;
  const innerRef = useRef<HTMLInputElement>(null);
  useImperativeHandle(ref, () => innerRef.current!, []);

  /**
   * Internal value is only used in uncontrolled mode.
   * When `value` is provided, we derive the rendered value from the prop instead.
   */
  const [internalValue, setInternalValue] = useState(
    typeof controlledValue === 'string' ? controlledValue : ''
  );
  const [touched, setTouched] = useState(false);
  const [validatorError, setValidatorError] = useState<string | null>(null);

  /** Whether the input is controlled by the parent (`value` prop provided). */
  const isControlled = controlledValue !== undefined;

  /**
   * The value currently shown in the input.
   * Comes from props when controlled, otherwise from internal state.
   */
  const value = isControlled ? controlledValue : internalValue;

  /**
   * Run all validators against the given value and store the first error, if any.
   * Required validation is handled separately via `requiredError`.
   */
  const runValidation = useCallback(
    (val: string) => {
      if (validators.length === 0) {
        setValidatorError(null);
        return;
      }
      const err = runValidators(val, validators, required);
      setValidatorError(err);
    },
    [validators, required]
  );

  // Required error is only computed once the field has been touched (blurred/invalid).
  const requiredError = required && touched && (value ?? '').trim() === '' ? requiredMessage : null;

  /**
   * Final error shown to the user.
   * Precedence:
   * - string `error` prop always wins (fully controlled error message).
   * - `error === true` marks the field invalid without a message.
   * - otherwise, fall back to required + validator errors.
   */
  const displayError =
    typeof controlledError === 'string'
      ? controlledError
      : controlledError === true
        ? ''
        : (requiredError ?? validatorError);

  /**
   * Handle low-level change events from the browser:
   * - read raw user input
   * - apply optional formatter
   * - update internal/controlled value
   * - re-run validation (after the field has been touched)
   * - notify the parent with a synthetic event that carries the formatted value
   */
  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      const previous = value ?? '';
      const next = formatter ? formatter(raw, previous) : raw;

      if (!isControlled) setInternalValue(next);
      if (touched && validators.length > 0) runValidation(next);

      const syntheticEvent = {
        ...e,
        target: { ...e.target, value: next },
      } as ChangeEvent<HTMLInputElement>;
      controlledOnChange?.(next, inputKey, syntheticEvent);
    },
    [value, formatter, isControlled, touched, validators.length, runValidation, controlledOnChange]
  );

  /**
   * On blur we mark the field as touched and run validation (if any validators are present).
   * This ensures required / validator errors only show after user interaction.
   */
  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      if (!suppressBlurValidation) {
        setTouched(true);
        if (validators.length > 0) runValidation(value ?? '');
      }
      onBlur?.(e);
    },
    [suppressBlurValidation, validators.length, value, runValidation, onBlur]
  );

  /**
   * Handle native browser `invalid` events (e.g. from HTML5 constraints).
   * We mark the field as touched and re-run custom validation,
   * then call any `onInvalid` handler provided by the parent.
   */
  const handleInvalid = useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      setTouched(true);
      runValidation(value ?? '');
      onInvalidProp?.(e);
    },
    [value, runValidation, onInvalidProp]
  );

  /**
   * Clear the current value (internal and/or controlled via `onChange`),
   * reset validation, and move focus back to the input.
   */
  const handleClear = useCallback(() => {
    if (!isControlled) setInternalValue('');
    runValidation('');
    const name = innerRef.current?.getAttribute?.('name') ?? undefined;
    const syntheticTarget = name != null ? { value: '', name } : { value: '' };
    controlledOnChange?.('', '', { target: syntheticTarget } as ChangeEvent<HTMLInputElement>);
    onClear?.();
    innerRef.current?.focus();
  }, [isControlled, runValidation, controlledOnChange, onClear]);

  const helperId = `${id}-helper`;

  /** Whether the field is currently considered invalid (for styling + a11y). */
  const hasError = Boolean(displayError);

  /**
   * When true, we render the right-side actions container
   * (clear button, rightButton, rightIcon).
   */
  const hasRightActions = (onClear && value) || rightButton != null || rightIcon != null;

  // Keep the native input's validity state in sync with our higher-level `displayError`.
  useEffect(() => {
    const el = innerRef.current;
    if (!el) return;
    el.setCustomValidity(hasError ? (displayError ?? DEFAULT_INVALID_MESSAGE) : '');
  }, [hasError, displayError]);

  /**
   * Notify the parent whenever our internal validation view changes,
   * unless the parent has taken full control by passing its own `error` prop.
   */
  useEffect(() => {
    const validationError =
      typeof controlledError === 'string' || controlledError === true
        ? null
        : (requiredError ?? validatorError);
    onValidationChange?.(validationError);
  }, [controlledError, requiredError, validatorError, onValidationChange]);

  return (
    <div
      ref={containerRef}
      className={cn(
        baseInputContainerVariants({ hasError, isDisabled: rest.disabled === true }),
        containerClassName
      )}
    >
      {leftIcon != null && <div className={leftIconWrapperClasses}>{leftIcon}</div>}

      {/*
       * Overlay layering strategy:
       * When an overlay is present (e.g. renderValue in Select), the overlay content
       * must drive the container's height so the input box grows to fit multi-line content.
       *
       * - The <input> becomes `absolute inset-0` so it no longer contributes to height
       *   but still covers the full area to capture clicks, focus, and keyboard events.
       *   Its text is made transparent so the overlay content is visible underneath.
       *
       * - The overlay <div> is in normal document flow (no absolute) with `pointer-events-none`
       *   so clicks pass through to the input. Its content naturally determines the height.
       *
       * When no overlay is present, the <input> stays in normal flow — zero regression.
       */}
      <div className="tw:relative tw:flex tw:flex-1 tw:min-w-0">
        <input
          ref={innerRef}
          id={id}
          value={value ?? ''}
          onChange={handleChange}
          onBlur={handleBlur}
          onInvalid={handleInvalid}
          required={required}
          pattern={restPattern}
          aria-invalid={hasError}
          aria-describedby={helperText || displayError ? helperId : undefined}
          className={cn(
            inputBaseClasses,
            // When overlay is active: pull input out of flow (absolute) so overlay drives height;
            // make text transparent so overlay content is visible.
            overlay != null && 'tw:absolute tw:inset-0 tw:text-transparent',
            inputClassName
          )}
          {...rest}
        />
        {/* Overlay: in normal flow so its height grows the container.
            pointer-events-none lets clicks pass through to the <input> above.
            min-h matches single-line input height as a baseline. */}
        {overlay != null && (
          <div className="tw:pointer-events-none tw:min-h-[1.25rem] tw:text-gray-darker">
            {overlay}
          </div>
        )}
      </div>

      {hasRightActions && (
        <div className={rightActionsWrapperClasses}>
          {onClear && value ? (
            <Button
              buttonType={ButtonType.text}
              variant={ButtonVariant.secondary}
              onClick={handleClear}
              aria-label={clearAriaLabel}
              className={clearButtonClasses}
            >
              <IoClose className="tw:h-5 tw:w-5 tw:max-h-5" aria-hidden />
            </Button>
          ) : null}
          {rightButton != null ? (
            <Button
              buttonType={ButtonType.text}
              variant={ButtonVariant.secondary}
              onClick={rightButton.onClick}
              className={rightActionButtonClasses}
              data-cy={rightButton['data-cy']}
            >
              {rightButton.label}
            </Button>
          ) : null}
          {rightIcon != null ? (
            <span className={rightIconWrapperClasses} aria-hidden>
              {rightIcon}
            </span>
          ) : null}
        </div>
      )}
    </div>
  );
});
